import React, {useState, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Segment, Icon, Table, Checkbox,
  Input, Button, Message, Label, Popup,
} from 'semantic-ui-react';
import {Actions} from './../../../../../common/actions';

const {getWtsEmployeesByCerts, createWtsMatriculas, getWtsMatriculas} = Actions;

const TruncatedCell = ({content, maxLength = 15}) => {
  if (!content) {
    return <span style={{'color': 'lightgrey'}}>N/A</span>;
  }
  const text = String(content);
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }
  
  return (
    <Popup
      content={text}
      trigger={
        <span
          style={{
            'cursor': 'pointer',
            'borderBottom': '1px dashed #ccc',
            'transition': 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#2185d0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'inherit';
          }}
        >
          {`${text.substring(0, maxLength)}...`}
        </span>
      }
    />
  );
};

// eslint-disable-next-line max-statements
const WtsMatriculaAbierta = ({courseToEdit, reduxData}) => {
  const dispatch = useDispatch();
  
  const {employeesByCerts, loading, employeeSelected, currentUser, enrolled} = useSelector(
    (st) => ({
      'employeesByCerts': st.wtsEmployees?.employeesByCerts || [],
      'loading': st.wtsEmployees?.loading || st.wtsMatriculas?.loading || false,
      'employeeSelected': st.wtsEmployees?.employeeSelected,
      'currentUser': st.session?.user || 'ADMIN',
      'enrolled': st.wtsMatriculas?.matriculas || [],
    })
  );

  const [state, setState] = useState({
    'selectedIds': [],
    'searchQuery': '',
    'sortColumn': 'names',
    'sortDirection': 'ascending',
  });

  const {selectedIds, searchQuery, sortColumn, sortDirection} = state;

  const config = {
    'courseId': courseToEdit?.id,
    'userRuc': employeeSelected?.ruc,
    'isOpen': courseToEdit?.status === 'A',
  };

  const targetCourseName = useMemo(() => {
    if (!courseToEdit || !reduxData?.courses) {
      return '';
    }
    const cId = courseToEdit.course_id || courseToEdit.idCursoReferencia;
    const foundCourse = reduxData.courses.find((c) => String(c.id) === String(cId));
    return foundCourse ? String(foundCourse.course || foundCourse.description || '').toUpperCase() : '';
  }, [courseToEdit, reduxData]);

  const currentDealer = useMemo(() => {
    if (!courseToEdit || !config.userRuc) {
      return null;
    }
    const rawDealers = courseToEdit.concesionarios ||
      courseToEdit.dealerParticipants ||
      courseToEdit.agendamiento_concesionarios || [];
      
    const dealerMatch = rawDealers.find(
      (d) => String(d.ruc).trim() === String(config.userRuc).trim()
    );

    if (!dealerMatch) {
      return null;
    }

    const officialDealer = (reduxData?.wtsDealers || []).find((wd) =>
      String(wd.ruc).trim() === String(dealerMatch.ruc).trim()
    );

    const dealerDesc = dealerMatch.description || dealerMatch.nombre;
    const offDesc = officialDealer ? officialDealer.description : null;

    return {
      'ruc': dealerMatch.ruc || '',
      'nombre': offDesc || dealerDesc || 'Sin identificar',
      'cupos': Number(dealerMatch.participantes || dealerMatch.count || dealerMatch.cupos || 0),
    };
  }, [courseToEdit, config.userRuc, reduxData]);

  const maxCupos = currentDealer?.cupos || 0;

  useEffect(() => {
    if (config.courseId) {
      setState((prev) => ({...prev, 'selectedIds': [], 'searchQuery': ''}));
      dispatch(getWtsMatriculas(config.courseId));
    }
  }, [config.courseId, dispatch]);

  useEffect(() => {
    if (Array.isArray(enrolled)) {
      const enrolledIds = enrolled.map((m) => Number(m.codemp || m.id));
      setState((prev) => ({...prev, 'selectedIds': enrolledIds}));
    }
  }, [enrolled]);

  useEffect(() => {
    if (courseToEdit && reduxData && config.isOpen) {
      const areaId = reduxData.areaSelected?.id || reduxData.areaSelected?.value || 1;
      const directedRaw = courseToEdit.directed_to || courseToEdit.destinatarios ||
        courseToEdit.course_destinatarios || [];

      const directedIds = directedRaw.map((d) => {
        if (typeof d === 'object' && d !== null) {
          return String(d.id || d.idCursoDestino || d.course_id_destino || d.course_id);
        }
        return String(d);
      });

      const certs = (reduxData.courses || [])
        .filter((c) => directedIds.includes(String(c.id)))
        .map((c) => String(c.course || c.description || ''))
        .filter((name) => name !== '');

      dispatch(getWtsEmployeesByCerts({
        'area': Number(areaId),
        'certificates': certs,
        'profile_id': 1,
        'ruc_filter': config.userRuc ? String(config.userRuc) : '',
      }));
    }
  }, [config.courseId, config.userRuc, dispatch, config.isOpen, courseToEdit, reduxData]);

  const validEmployees = useMemo(() => {
    if (!targetCourseName) {
      return employeesByCerts;
    }
    return employeesByCerts.filter((emp) => {
      const empCerts = (emp.certificates || []).map((c) => String(c).toUpperCase());
      return !empCerts.includes(targetCourseName);
    });
  }, [employeesByCerts, targetCourseName]);

  const filteredData = useMemo(() => {
    if (!searchQuery) {
      return validEmployees;
    }
    const q = searchQuery.toLowerCase();
    return validEmployees.filter((emp) =>
      String(emp.id || '').toLowerCase().includes(q) ||
      String(emp.names || '').toLowerCase().includes(q) ||
      String(emp.last_names || '').toLowerCase().includes(q) ||
      String(emp.document || '').toLowerCase().includes(q) ||
      String(emp.agency_description || emp.agency || '').toLowerCase().includes(q) ||
      String(emp.charge || '').toLowerCase().includes(q) ||
      String(emp.ruc || '').toLowerCase().includes(q) ||
      String(emp.start_date || '').toLowerCase().includes(q) ||
      String(emp.email || '').toLowerCase().includes(q) ||
      String(emp.user || '').toLowerCase().includes(q) ||
      String(emp.profile_description || '').toLowerCase().includes(q) ||
      (emp.certificates || []).some((cert) => String(cert).toLowerCase().includes(q))
    );
  }, [validEmployees, searchQuery]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aSelected = selectedIds.includes(a.id);
      const bSelected = selectedIds.includes(b.id);
      
      if (aSelected && !bSelected) {
        return -1;
      }
      if (!aSelected && bSelected) {
        return 1;
      }

      const valA = String(a[sortColumn] || '').toLowerCase();
      const valB = String(b[sortColumn] || '').toLowerCase();
      if (valA < valB) {
        return sortDirection === 'ascending' ? -1 : 1;
      }
      if (valA > valB) {
        return sortDirection === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection, selectedIds]);

  const updateState = (updates) => setState((prev) => ({...prev, ...updates}));

  const handleSort = (clickedColumn) => {
    if (sortColumn !== clickedColumn) {
      updateState({'sortColumn': clickedColumn, 'sortDirection': 'ascending'});
      return;
    }
    updateState({'sortDirection': sortDirection === 'ascending' ? 'descending' : 'ascending'});
  };

  const handleSelectAll = (e, {checked}) => {
    if (checked) {
      updateState({'selectedIds': sortedData.slice(0, maxCupos).map((emp) => emp.id)});
    } else {
      updateState({'selectedIds': []});
    }
  };

  const handleSelectOne = (e, {checked, value}) => {
    if (checked) {
      if (selectedIds.length < maxCupos) {
        updateState({'selectedIds': [...selectedIds, value]});
      }
    } else {
      updateState({'selectedIds': selectedIds.filter((id) => id !== value)});
    }
  };

  const handleSave = () => {
    if (!config.courseId || selectedIds.length === 0) {
      return;
    }

    dispatch(createWtsMatriculas({
      'codemps': selectedIds,
      'id_agenda': config.courseId,
      'usuario_creacion': currentUser,
    }));
  };

  return (
    <Segment
      padded="very"
      loading={loading}
      style={{'minHeight': '400px', 'display': 'flex', 'flexDirection': 'column'}}
    >
      <div
        style={{
          'backgroundColor': '#f8f9fa',
          'padding': '15px 20px',
          'borderRadius': '8px',
          'border': '1px solid #e0e0e0',
          'marginBottom': '20px',
          'display': 'flex',
          'justifyContent': 'space-between',
          'alignItems': 'center',
          'boxShadow': '0 2px 4px rgba(0,0,0,0.02)',
        }}
      >
        <div>
          <div
            style={{'margin': '0 0 8px 0', 'color': '#333', 'fontWeight': 'bold', 'fontSize': '1.1em'}}
          >
            <Icon name="user" style={{'color': '#333'}} /> Estudiantes que cumplen los requisitos
          </div>
          {config.isOpen && (
            <div
              style={{
                'margin': '0',
                'display': 'flex',
                'alignItems': 'center',
                'gap': '12px',
                'color': '#555',
                'fontSize': '1.1em',
              }}
            >
              {currentDealer ? (
                <>
                  <span>
                    Concesionario: <strong>{currentDealer.ruc}</strong> - {currentDealer.nombre}
                  </span>
                  <span
                    style={{
                      'backgroundColor': '#eef6fc',
                      'padding': '6px 12px',
                      'borderRadius': '6px',
                      'border': '1px solid #cce2ff',
                      'fontWeight': 'bold',
                    }}
                  >
                    Cupos:{' '}
                    <strong
                      style={{
                        'color': selectedIds.length === maxCupos ? '#db2828' : '#21ba45',
                        'fontSize': '1.1em',
                        'marginLeft': '4px',
                      }}
                    >
                      {selectedIds.length} / {maxCupos}
                    </strong>
                  </span>
                </>
              ) : (
                <>
                  <span>Concesionario no identificado</span>
                  <span
                    style={{
                      'backgroundColor': '#fff2f2',
                      'padding': '6px 12px',
                      'borderRadius': '6px',
                      'border': '1px solid #ffcccc',
                      'fontWeight': 'bold',
                      'color': '#db2828',
                    }}
                  >
                    Cupos: {selectedIds.length} / 0
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        {config.isOpen && (
          <Input
            icon="search"
            placeholder="Buscar estudiantes..."
            value={searchQuery}
            onChange={(e, {value}) => updateState({'searchQuery': value})}
            style={{'width': '250px'}}
          />
        )}
      </div>

      {!config.isOpen ? (
        <div
          style={{
            'flex': 1, 'display': 'flex', 'alignItems': 'center', 'justifyContent': 'center',
          }}
        >
          <Message
            error
            icon="comment alternate outline"
            header="Matriculación no disponible"
            content="El curso debe estar ABIERTO."
          />
        </div>
      ) : (
        <>
          <div style={{'flex': 1, 'overflowY': 'auto', 'overflowX': 'auto'}}>
            <Table
              sortable
              celled
              compact="very"
              size="small"
              selectable
              textAlign="center"
              style={{'fontSize': '0.75em'}}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell collapsing>
                    <Checkbox
                      onChange={handleSelectAll}
                      checked={sortedData.length > 0 && selectedIds.length > 0}
                      disabled={maxCupos === 0}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'id' ? sortDirection : null}
                    onClick={() => handleSort('id')}
                  >
                    Cód
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'document' ? sortDirection : null}
                    onClick={() => handleSort('document')}
                  >
                    Cédula
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'names' ? sortDirection : null}
                    onClick={() => handleSort('names')}
                  >
                    Nombres
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'last_names' ? sortDirection : null}
                    onClick={() => handleSort('last_names')}
                  >
                    Apellidos
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'agency_description' ? sortDirection : null}
                    onClick={() => handleSort('agency_description')}
                  >
                    Agencia
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'start_date' ? sortDirection : null}
                    onClick={() => handleSort('start_date')}
                  >
                    Ingreso
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    Certificados
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'charge' ? sortDirection : null}
                    onClick={() => handleSort('charge')}
                  >
                    Cargo
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'profile_description' ? sortDirection : null}
                    onClick={() => handleSort('profile_description')}
                  >
                    Perfil
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'email' ? sortDirection : null}
                    onClick={() => handleSort('email')}
                  >
                    Correo
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortColumn === 'user' ? sortDirection : null}
                    onClick={() => handleSort('user')}
                  >
                    User
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {sortedData.length > 0 ? (
                  sortedData.map((emp) => {
                    const isChecked = selectedIds.includes(emp.id);
                    const isDisabled = !isChecked && selectedIds.length >= maxCupos;
                    return (
                      <Table.Row
                        key={emp.id}
                        disabled={isDisabled}
                        style={{'backgroundColor': isChecked ? '#f2fcf5' : 'inherit'}}
                      >
                        <Table.Cell collapsing>
                          <Checkbox
                            value={emp.id}
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={handleSelectOne}
                          />
                        </Table.Cell>
                        <Table.Cell>{emp.id}</Table.Cell>
                        <Table.Cell>{emp.document}</Table.Cell>
                        <Table.Cell textAlign="left">
                          <TruncatedCell content={emp.names} maxLength={12} />
                        </Table.Cell>
                        <Table.Cell textAlign="left">
                          <TruncatedCell content={emp.last_names} maxLength={12} />
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedCell
                            content={emp.agency_description || emp.agency}
                            maxLength={10}
                          />
                        </Table.Cell>
                        <Table.Cell>{emp.start_date}</Table.Cell>
                        <Table.Cell style={{'maxWidth': '150px'}} textAlign="left">
                          {emp.certificates && emp.certificates.length > 0 ? (
                            <div style={{'display': 'flex', 'flexWrap': 'wrap', 'gap': '2px'}}>
                              {emp.certificates.map((cert, index) => (
                                <Popup
                                  key={index}
                                  content={cert}
                                  trigger={
                                    <Label
                                      size="mini"
                                      color="teal"
                                      basic
                                      style={{
                                        'margin': 0, 'cursor': 'pointer', 'padding': '0.3em',
                                        'transition': 'all 0.2s ease',
                                        'backgroundColor': 'transparent',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#e0f2f1';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      {cert.length > 10 ? `${cert.substring(0, 10)}...` : cert}
                                    </Label>
                                  }
                                />
                              ))}
                            </div>
                          ) : (
                            <span style={{'color': 'lightgrey'}}>N/A</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedCell content={emp.charge} maxLength={10} />
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedCell content={emp.profile_description} maxLength={10} />
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedCell content={emp.email} maxLength={12} />
                        </Table.Cell>
                        <Table.Cell>
                          <TruncatedCell content={emp.user} maxLength={8} />
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                ) : (
                  <Table.Row>
                    <Table.Cell colSpan="12" textAlign="center" style={{'padding': '20px'}}>
                      <span style={{'color': 'grey'}}>
                        {searchQuery ? 'No hay resultados.' : 'No hay estudiantes disponibles.'}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          <div style={{'marginTop': '15px', 'textAlign': 'right'}}>
            <Button
              color="green"
              icon="save"
              content="Guardar"
              labelPosition="left"
              onClick={handleSave}
              disabled={selectedIds.length === 0}
            />
          </div>
        </>
      )}
    </Segment>
  );
};

export default WtsMatriculaAbierta;
